import {z} from "zod";

// Logs
// Event Log Schema
export const logEventSchema = z.object({
  type: z.string().min(1, "Event type is required"),
  timestamp: z.string().datetime("Invalid timestamp format"),
  userId: z.string().optional(),
  url: z.string().min(1, "URL is required"),
  metadata: z.record(z.string(), z.any()).optional(),
});

// Error Log Schema
export const logErrorSchema = z.object({
  type: z.string().min(1, "Error type is required"),
  timestamp: z.string().datetime("Invalid timestamp format"),
  userId: z.string().optional(),
  url: z.string().min(1, "URL is required"),
  message: z.string().min(1, "Error message is required"),
  stack: z.string().nullable().optional(),
  metadata: z.record(z.string(), z.any()).optional(),
});

// Org Species
export const orgSpeciesStatus = [
  "ANNUAL",
  "RESIDENT",
  "UNCOMMON",
  "VAGRANT",
  "SCARCE",
  "RARE",
] as const;

export const addOrgSpeciesSchema = z.object({
  speciesId: z.string().min(1, "Species ID is required"),
  status: z.enum(orgSpeciesStatus),
  note: z.string().optional(),
});

export const updateOrgSpeciesSchema = z.object({
  status: z.enum(orgSpeciesStatus),
  note: z.string().optional(),
});

// Auth
// Password Schema (used when setting/changing a password)
export const passwordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters")
  .max(128, "Password must be at most 128 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number");

// Login
// Only require that a password is present; applying the full complexity policy
// here can reject otherwise-valid credentials and leaks the policy to clients.
export const loginSchema = z.object({
  email: z.string().email(),
  password: z.string().min(1, "Password is required"),
});

// Registration
export const registerSchema = z.object({
  email: z.string().email(),
  name: z.string().min(1).max(100),
  password: passwordSchema,
  agreedToTerms: z.literal(true, {
    errorMap: () => ({message: "You must agree to the Terms of Service, Privacy Policy, and Cookie Policy"}),
  }),
});

// Request Password Reset
export const requestPasswordResetSchema = z.object({
  email: z.string().email(),
});

// Reset Password
export const resetPasswordSchema = z.object({
  token: z.string(),
  password: passwordSchema,
});

// Events
// Create Event
export const createEventSchema = z.object({
  name: z.string().min(1, "Event name is required"),
  description: z.string().min(1, "Event description is required"),
  startDate: z
    .string()
    // .datetime("Invalid start date format")
    .refine((x) => {
      console.log("x:", x);
      return true;
    }),
});

// Contact Requests
// Create Contact Request
export const newContactRequestSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().email(),
  message: z.string().max(2000),
});

// Close Contact Request
export const closeContactRequestSchema = z.object({
  closeMessage: z.string().optional(),
});

// Organization
// Create Organization
export const createOrgSchema = z.object({
  name: z.string().min(1, "Organization name is required"),
  slug: z
    .string()
    .min(1, "Organization slug is required")
    .regex(
      /^[a-z0-9-]+$/,
      "Slug can only contain lowercase letters, numbers, and hyphens"
    ),
  telegramChatId: z.string().optional(),
});

// Update Organization
export const updateOrgSchema = z.object({
  name: z.string().min(1, "Organization name is required").optional(),
  slug: z
    .string()
    .regex(
      /^[a-z0-9-]+$/,
      "Slug can only contain lowercase letters, numbers, and hyphens"
    )
    .optional(),
  telegramChatId: z.string().optional(),
});

// Record
// New Record
export const newRecordSchema = z.object({
  orgId: z.string().min(1, "Organization ID is required"),
  speciesId: z.string().min(1, "Species ID is required"),
  note: z.string().optional(),
  locationNote: z.string().optional(),
  siteId: z.string().optional(),
  dtm: z.string(),
  lat: z.number().optional(),
  lng: z.number().optional(),
  count: z.number().optional(),
});

// Update Record
export const updateRecordSchema = z.object({
  speciesId: z.string().min(1, "Species is required"),
  note: z.string().nullable(),
  locationNote: z.string().nullable(),
  siteId: z.string().nullable(),
  dtm: z.string(),
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  count: z.number().nullable(),
});

// Record Asset
// Create Record Asset
export const createAssetSchema = z.object({
  type: z.enum(["IMAGE"]),
  mimeType: z.string(),
  assetBy: z.string(),
  note: z.string().optional(),
  fileName: z.string(),
});

// Invited User
export const createRequestedUserSchema = z.object({
  email: z.string().email(),
});

// User
// Create System User
export const createSystemUserSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  name: z.string().min(1, "User name is required"),
  sendWelcomeEmail: z.boolean(),
});

export const editSystemUserSchema = z.object({
  email: z.string().email("Please enter a valid email"),
  name: z.string().min(1, "User name is required"),
});
